import { useEffect, useState } from 'react';
import type { CardData, Suit } from '../game/types';
import { validateDeckComposition } from '../game/deck';
import { MapView, ExtractActionOverlay, NodeInfoOverlay, type RunMap } from './MapView';
import { ReconfigureOverlay } from './ReconfigureOverlay';
import { useDialog } from './Dialog';
import { RunTutorialOverlay } from './RunTutorialOverlay';
import { Card } from './Board';
import { trackEvent } from '../utils/telemetry';

import { SERVER_ORIGIN } from '../config';

interface RunDeckSnapshot {
  deckId: string;
  name: string;
  cards: CardData[];
  valid: boolean;
}

export interface UserRunRecord {
  id: string;
  userId: string;
  status: 'active' | 'failed' | 'abandoned' | 'extracted';
  decksSnapshot: { A: RunDeckSnapshot; B: RunDeckSnapshot };
  resourcesAcc: Record<string, number>;
  cardsAcc: CardData[];
  map: RunMap | null;
  startedAt: string;
  endedAt: string | null;
  // Vittoria registrata sul nodo, in attesa dell'avanzamento.
  pendingAdvanceNodeId?: string | null;
  // Partita della run in corso (una sola alla volta).
  activeMatch?: { matchID: string; targetNodeId: string; deckKey: 'A' | 'B' } | null;
  // Esito dell'ultima partita registrata (anche abbandono / timeout).
  lastMatchResult?: {
    matchID: string;
    outcome: 'won' | 'lost' | 'draw';
    reason: 'played' | 'forfeit' | 'interrupted';
    targetNodeId: string | null;
    cardsLost: Array<{ suit: string; value: number; rarity: string }>;
    cardsLooted: number;
    at: string;
  } | null;
}

interface UserDeckSummary {
  id: string;
  name: string;
  createdAt: string;
  cards: { id: string; card: CardData }[];
}

interface RunScreenProps {
  run: UserRunRecord;
  token: string;
  user: {
    settings?: {
      runTutorialSeen?: boolean;
    };
  };
  onUpdateSettings: (settings: { runTutorialSeen?: boolean }) => Promise<void>;
  onAbandoned: () => void;       // run terminata volontariamente → torna lobby
  // Avvia il match dentro la run. targetNodeId = nodo destinazione (per
  // advance-to-node post-win). deckKey + deckCards: scelta dal picker.
  onStartMatch: (deckKey: 'A' | 'B', deckCards: CardData[], targetNodeId: string) => void;
  onRefresh: (run: UserRunRecord | null) => void; // chiamato dopo ogni mutazione
  // Chiamato a extract success: il server ha appena bankato `gained` nella
  // stash dell'utente, totale aggiornato in `banked`. App.tsx usa questo per
  // aggiornare il proprio AuthUser.bankedResources locale + persistere.
  onResourcesBanked?: (gained: Record<string, number>, banked: Record<string, number>, gainedCardIds: string[]) => void;
  // Riprende la partita della run in corso (se le credenziali sono su questo
  // browser). Ritorna false se non è possibile riprenderla da qui.
  onResumeMatch?: (matchID: string) => boolean;
}

export function RunScreen({ run, token, user, onUpdateSettings, onAbandoned, onStartMatch, onRefresh, onResourcesBanked, onResumeMatch }: RunScreenProps) {
  const dialog = useDialog();
  const [inspectedNodeId, setInspectedNodeId] = useState<string | null>(null);
  const [extractOpen, setExtractOpen] = useState(false);
  const [reconfigureOpen, setReconfigureOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractGains, setExtractGains] = useState<{
    gained: Record<string, number>;
    banked: Record<string, number>;
  } | null>(null);

  // Post-run card reveal and detailed debrief states
  const [revealStage, setRevealStage] = useState<'idle' | 'revealing' | 'complete'>('idle');
  const [revealIndex, setRevealIndex] = useState<number>(0);
  const [revealedCards, setRevealedCards] = useState<CardData[]>([]);

  useEffect(() => {
    if (run.status === 'extracted' && run.cardsAcc && run.cardsAcc.length > 0 && revealStage === 'idle') {
      setRevealedCards(run.cardsAcc);
      setRevealStage('revealing');
      setRevealIndex(0);
    }
  }, [run.status, run.cardsAcc, revealStage]);

  useEffect(() => {
    if (run.status === 'failed') {
      trackEvent('run_failed', { runId: run.id });
    }
  }, [run.id, run.status]);

  const formatDuration = (startStr: string, endStr: string | null) => {
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : new Date();
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return '0s';
    const diffSec = Math.floor(diffMs / 1000);
    const m = Math.floor(diffSec / 60);
    const s = diffSec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const getSectorsCompleted = (runRecord: UserRunRecord) => {
    if (!runRecord.map) return 0;
    return runRecord.map.nodes.filter(n => n.visited).length;
  };

  // Click su QUALSIASI nodo → apre il NodeInfoOverlay (inspection + eventuali azioni).
  const handleNodeClick = (nodeId: string) => {
    setInspectedNodeId(nodeId);
  };

  // Dal NodeInfoOverlay, click su deck A/B (nodo regular/boss adiacente).
  const handlePlayMatchFromNodeOverlay = async (deckKey: 'A' | 'B') => {
    if (!inspectedNodeId) return;
    if (run.activeMatch) {
      setInspectedNodeId(null);
      const resume = await dialog.confirm({
        title: 'Match already in progress',
        message: `You have an unfinished match at ${nodeLabel(run.activeMatch.targetNodeId)}. Only one run match at a time: resume it, or forfeit it first (a forfeit counts as a loss).`,
        okLabel: 'Resume it',
        cancelLabel: 'Close',
      });
      if (resume) await resumeMatch();
      return;
    }
    const snap = run.decksSnapshot[deckKey];
    if (!snap.valid) {
      setError('Selected deck is invalid');
      return;
    }
    const target = inspectedNodeId;
    setInspectedNodeId(null);
    onStartMatch(deckKey, snap.cards, target);
  };

  // Dal NodeInfoOverlay, click "vai" su extract node adiacente.
  const handleAdvanceToExtractFromNodeOverlay = async () => {
    if (!inspectedNodeId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/advance-to-node`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nodeId: inspectedNodeId }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error ?? 'move failed'); return; }
      trackEvent('run_node_entered', { nodeId: inspectedNodeId, runId: json.run.id });
      onRefresh(json.run as UserRunRecord);
      setInspectedNodeId(null);
      setExtractOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  // Nodo di estrazione su cui si trova il giocatore (per il Safehouse cache).
  const currentExtractNode = run.map?.nodes.find(n => n.id === run.map!.currentNodeId && n.type === 'extract');

  // Safehouse cache: prende 1 delle 3 carte offerte dal nodo di estrazione.
  const claimCache = async (index: number) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/claim-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ index }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error ?? 'could not take the card'); return; }
      trackEvent('run_cache_claimed', { runId: json.run.id, index });
      onRefresh(json.run as UserRunRecord);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  const doExtract = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/extract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error ?? 'extraction failed'); return; }
      const gained = (json.resourcesGained ?? {}) as Record<string, number>;
      const banked = (json.bankedResources ?? {}) as Record<string, number>;
      const gainedCardIds = (json.gainedCardIds ?? []) as string[];
      trackEvent('run_extracted', {
        runId: json.run.id,
        cardsGainedCount: gainedCardIds.length,
        resourcesGained: gained,
      });
      setExtractGains({ gained, banked });
      onResourcesBanked?.(gained, banked, gainedCardIds);
      onRefresh(json.run as UserRunRecord);
      setExtractOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  // --- partita della run in corso / vittoria da riscattare / ultimo esito ---
  const nodeLabel = (id: string | null | undefined) => {
    const n = id ? run.map?.nodes.find(x => x.id === id) : undefined;
    if (!n) return 'this node';
    return n.type === 'boss' ? 'the boss node' : `the depth ${n.depth} node (${n.difficulty})`;
  };

  const LOSS_RULES = "It counts as a LOSS: any cards the opponent snatched are lost, and if they snatched none, one random card of the deck you used is sacrificed. That deck stays invalid until you repair it at a Safehouse.";

  const forfeitMatch = async () => {
    if (busy || !run.activeMatch) return;
    const ok = await dialog.confirm({
      title: 'Forfeit the match?',
      message: `You're forfeiting the match at ${nodeLabel(run.activeMatch.targetNodeId)}.\n${LOSS_RULES}`,
      okLabel: 'Forfeit (count as loss)',
      cancelLabel: 'Keep it',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/forfeit-match`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (!res.ok) { setError(j.error ?? 'forfeit failed'); return; }
      trackEvent('run_match_forfeited', { runId: run.id });
      onRefresh(j.run as UserRunRecord);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  const resumeMatch = async () => {
    if (!run.activeMatch) return;
    const resumed = onResumeMatch?.(run.activeMatch.matchID) ?? false;
    if (!resumed) {
      await dialog.alert({
        title: "Can't resume here",
        message: "This match was started on another browser or device, so it can't be resumed from here. Resume it there, or forfeit it (it counts as a loss).",
      });
    }
  };

  // Vittoria registrata ma avanzamento mai fatto (es. scheda chiusa subito
  // dopo aver vinto): il giocatore avanza da qui.
  const advancePending = async () => {
    if (busy || !run.pendingAdvanceNodeId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/advance-to-node`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nodeId: run.pendingAdvanceNodeId }),
      });
      const j = await res.json();
      if (!res.ok) { setError(j.error ?? 'advance failed'); return; }
      if (j.bossCleared) onResourcesBanked?.(j.resourcesGained ?? {}, j.bankedResources ?? {}, j.gainedCardIds ?? []);
      onRefresh(j.run as UserRunRecord);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  // Riquadro "ultima partita": si nasconde quando il giocatore lo chiude
  // (ricordato per matchID su questo browser).
  const LAST_SEEN_KEY = 'gs_run_last_result_seen';
  const [lastSeen, setLastSeen] = useState<string | null>(() => {
    try { return localStorage.getItem(LAST_SEEN_KEY); } catch { return null; }
  });
  const dismissLastResult = () => {
    const id = run.lastMatchResult?.matchID ?? null;
    setLastSeen(id);
    try { if (id) localStorage.setItem(LAST_SEEN_KEY, id); } catch { /* ignore */ }
  };

  const abandon = async () => {
    if (busy) return;
    const ok = await dialog.confirm({
      title: 'Abandon the run?',
      message: "You'll lose all accumulated cards and resources from this run.\nYour base decks stay intact (minus cards already lost to snatch or defeat).",
      okLabel: 'Abandon',
      cancelLabel: 'Continue the run',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/abandon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json();
        setError(j.error ?? 'abandon failed');
      } else {
        trackEvent('run_abandoned', { runId: run.id });
        onAbandoned();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  // Card Reveal overlay for extracted runs containing accumulated cards
  if (run.status === 'extracted' && revealStage === 'revealing' && revealedCards.length > 0) {
    const currentCard = revealedCards[revealIndex];
    const isLast = revealIndex >= revealedCards.length - 1;
    const rarity = currentCard.rarity ?? 'street';

    let glowShadow = '0 0 30px var(--accent-color), 0 0 60px rgba(79, 195, 247, 0.3)';
    let rarityLabel = 'STREET CARD';
    let rarityColor = '#4fc3f7';

    if (rarity === 'kingpin') {
      glowShadow = '0 0 40px #ffb703, 0 0 80px rgba(255, 183, 3, 0.5)';
      rarityLabel = 'KINGPIN CARD';
      rarityColor = '#ffb703';
    } else if (rarity === 'made') {
      glowShadow = '0 0 35px #b5179e, 0 0 70px rgba(181, 23, 158, 0.5)';
      rarityLabel = 'MADE CARD';
      rarityColor = '#b5179e';
    }

    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(5, 5, 10, 0.94)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        zIndex: 9000, overflow: 'auto', boxSizing: 'border-box', padding: 24,
      }}>
        <div style={{
          position: 'absolute', width: 280, height: 280, borderRadius: '50%',
          background: rarityColor, filter: 'blur(120px)', opacity: 0.15, pointerEvents: 'none',
        }} />

        <h2 style={{ color: '#fff', fontSize: 20, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 2 }}>
          Loot Secured
        </h2>
        <div style={{ color: rarityColor, fontSize: 11, fontWeight: 'bold', letterSpacing: 2, marginBottom: 32 }}>
          {rarityLabel}
        </div>

        <div style={{
          width: 80, height: 120, transform: 'scale(2.2)', transformOrigin: 'center',
          boxShadow: glowShadow, borderRadius: 8, transition: 'all 0.4s ease',
          marginBottom: 96, marginTop: 48, position: 'relative',
        }}>
          <Card card={currentCard} staticContext />
        </div>

        <div style={{
          background: '#0d0d16', border: '1px solid #222', borderRadius: 8,
          padding: '16px 20px', width: '100%', maxWidth: 360, textAlign: 'center',
          boxSizing: 'border-box', marginBottom: 32, zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#fff', textTransform: 'capitalize' }}>
            {currentCard.suit} - {currentCard.type === 'Mission' ? 'Mission' : `Value ${currentCard.value}`}
          </div>
          {currentCard.effects && currentCard.effects.length > 0 ? (
            <div style={{ marginTop: 10, borderTop: '1px solid #1f1f2e', paddingTop: 8 }}>
              {currentCard.effects.map((e, idx) => (
                <div key={idx} style={{ color: '#aaa', fontSize: 12, fontStyle: 'italic', lineHeight: 1.4 }}>
                  {e.text}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#666', fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>
              Standard street crew asset.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 24, zIndex: 1 }}>
          <button
            onClick={() => setRevealStage('complete')}
            style={{
              background: 'transparent', border: 'none', color: '#666',
              cursor: 'pointer', fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            Skip Reveal
          </button>
          <button
            onClick={() => {
              if (isLast) {
                setRevealStage('complete');
              } else {
                setRevealIndex(i => i + 1);
              }
            }}
            className="multi-btn"
            style={{ fontSize: 13, padding: '8px 24px', minWidth: 120 }}
          >
            {isLast ? 'Done' : 'Next Card →'}
          </button>
        </div>

        <div style={{ marginTop: 20, color: '#444', fontSize: 11, fontWeight: 'bold', letterSpacing: 1 }}>
          CARD {revealIndex + 1} OF {revealedCards.length}
        </div>
      </div>
    );
  }

  // Detailed Gameover Debrief for extracted/failed runs
  if (run.status === 'extracted' || run.status === 'failed') {
    const isSuccess = run.status === 'extracted';
    // Run chiusa battendo il boss (ultimo nodo) invece che a un Safehouse.
    const bossCleared = isSuccess && run.map?.nodes.find(n => n.id === run.map!.currentNodeId)?.type === 'boss';
    const sectors = getSectorsCompleted(run);
    const duration = formatDuration(run.startedAt, run.endedAt);
    const resources = extractGains?.gained ?? run.resourcesAcc ?? {};
    const resourcesEntries = Object.entries(resources).filter(([, n]) => n > 0);

    return (
      <div style={{
        padding: '24px 32px', maxWidth: 960, margin: '0 auto', overflowY: 'auto',
        height: '100%', boxSizing: 'border-box', color: '#fff',
      }}>
        <div style={{
          textAlign: 'center', marginBottom: 32, padding: '24px 16px',
          background: isSuccess ? 'rgba(255, 183, 3, 0.05)' : 'rgba(229, 115, 115, 0.05)',
          border: isSuccess ? '1px solid rgba(255, 183, 3, 0.2)' : '1px solid rgba(229, 115, 115, 0.2)',
          borderRadius: 8,
        }}>
          <h1 style={{
            color: isSuccess ? '#ffb703' : '#e57373', fontSize: 26, margin: 0,
            textTransform: 'uppercase', letterSpacing: 2,
            textShadow: isSuccess ? '0 0 10px rgba(255, 183, 3, 0.3)' : '0 0 10px rgba(229, 115, 115, 0.3)',
          }}>
            {bossCleared ? '👑 The Big Score — Boss Defeated' : isSuccess ? '💰 Extraction Success — Heist Complete' : '🚨 Operation Compromised — Run Failed'}
          </h1>
          <p style={{ color: '#aaa', fontSize: 13, marginTop: 8, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            {bossCleared
              ? 'You took down the boss and walked out with everything. The boss stash and all accumulated cards have been transferred to your permanent collection and active decks updated.'
              : isSuccess
              ? 'You successfully extracted from Milan\'s underground map. All accumulated cards have been transferred to your permanent collection and active decks updated.'
              : run.lastMatchResult?.reason === 'forfeit'
                ? 'Both of your active decks became invalid: your last match was forfeited and counted as a loss. The operation has been terminated and all accumulated run loot was lost.'
                : run.lastMatchResult?.reason === 'interrupted'
                  ? 'Both of your active decks became invalid: your last match was left unfinished for over an hour and counted as a loss. The operation has been terminated and all accumulated run loot was lost.'
                  : 'Both of your active decks became invalid due to card losses in combat. The operation has been terminated and all accumulated assets were lost.'}
          </p>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 24, marginBottom: 32,
        }}>
          {/* Operation Metrics Card */}
          <div style={{
            background: '#15151f', border: '1px solid #2a2a3a', borderRadius: 8, padding: 20,
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: isSuccess ? '#ffb703' : '#e57373', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
              Operation Metrics
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #1f1f2a', paddingBottom: 8 }}>
                <span style={{ color: '#888', fontSize: 12 }}>Time Elapsed</span>
                <span style={{ color: '#eee', fontSize: 13, fontWeight: 'bold' }}>{duration}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #1f1f2a', paddingBottom: 8 }}>
                <span style={{ color: '#888', fontSize: 12 }}>Sectors Completed</span>
                <span style={{ color: '#eee', fontSize: 13, fontWeight: 'bold' }}>{sectors} / 12</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: '#888', fontSize: 12 }}>{isSuccess ? 'Loot Banked' : 'Loot Lost'}</span>
                {resourcesEntries.length === 0 ? (
                  <span style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>{isSuccess ? 'No resources accumulated.' : 'Any resources gathered in this run were lost.'}</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {resourcesEntries.map(([suit, count]) => (
                      <div
                        key={suit}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 8px', background: '#0e0e16',
                          border: '1px solid #222', borderRadius: 4,
                        }}
                      >
                        <span className={`suit-icon suit-icon-${suit.replace(/\s+/g, '-')}`} style={{ width: 16, height: 16 }} />
                        <span style={{ color: isSuccess ? '#4CAF50' : '#e57373', fontWeight: 'bold', fontSize: 13 }}>+{count}</span>
                        <span style={{ color: '#777', fontSize: 9, textTransform: 'capitalize' }}>{suit}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Final Deck Status Card */}
          <div style={{
            background: '#15151f', border: '1px solid #2a2a3a', borderRadius: 8, padding: 20,
            display: 'flex', flexDirection: 'column',
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#ddd', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
              Final Deck Status
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
              {(['A', 'B'] as const).map(key => {
                const deck = run.decksSnapshot[key];
                return (
                  <div key={key} style={{ background: '#0e0e16', border: '1px solid #222', borderRadius: 6, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 'bold', color: '#fff' }}>Deck {key} ({deck.name})</span>
                      <span style={{
                        fontSize: 9, fontWeight: 'bold', padding: '2px 8px', borderRadius: 3,
                        background: deck.valid ? 'rgba(76, 175, 80, 0.15)' : 'rgba(229, 115, 115, 0.15)',
                        color: deck.valid ? '#4CAF50' : '#e57373',
                        border: deck.valid ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(229, 115, 115, 0.3)',
                      }}>
                        {deck.valid ? 'VALID' : 'COMPROMISED'}
                      </span>
                    </div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 8 }}>
                      {deck.cards.length} cards remaining
                    </div>
                    {deck.cards.length > 0 ? (
                      <div className="mini-deck-scroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                        {deck.cards.map((c, i) => (
                          <div key={i} style={{ width: 32, height: 48, flexShrink: 0, overflow: 'visible' }}>
                            <div style={{ transform: 'scale(0.4)', transformOrigin: 'top left' }}>
                              <Card card={c} staticContext />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: '#555', fontSize: 11, fontStyle: 'italic' }}>No cards left.</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Secured Cards Gained Grid (Success only) */}
        {isSuccess && run.cardsAcc && run.cardsAcc.length > 0 && (
          <div style={{
            background: '#15151f', border: '1px solid #2a2a3a', borderRadius: 8, padding: 20,
            marginBottom: 32,
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#ffb703', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
              Secured Cards Gained ({run.cardsAcc.length})
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {run.cardsAcc.map((c, idx) => (
                <div key={idx} style={{ width: 64, height: 96, flexShrink: 0, overflow: 'visible' }}>
                  <div style={{ transform: 'scale(0.8)', transformOrigin: 'top left' }}>
                    <Card card={c} staticContext />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24, marginBottom: 48 }}>
          <button onClick={onAbandoned} className="multi-btn" style={{ padding: '10px 32px', fontSize: 14 }}>
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Padding override come shorthand pieno (24 in alto/lati, 48 in basso).
          Mescolare `padding: 24` ereditato da panelStyle con un `paddingBottom`
          longhand qui causa il warning React "Removing a style property during
          rerender" quando il pannello transita verso lo stato extracted/failed
          (che usa solo panelStyle senza override). */}
      <div style={{ ...panelStyle, padding: '24px 24px 48px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <h1 style={{ color: 'var(--accent-color)', margin: 0 }}>Run in progress</h1>
            <p style={{ color: '#888', fontSize: 13, marginTop: 4, marginBottom: 0 }}>
              Started on {new Date(run.startedAt).toLocaleString()}
            </p>
          </div>
          <button
            onClick={abandon}
            disabled={busy}
            style={{ ...btnDanger, padding: '6px 14px', fontSize: 12, flexShrink: 0 }}
          >Abandon run</button>
        </div>

        {error && (
          <div style={{ color: '#e57373', marginTop: 8, fontSize: 13 }}>{error}</div>
        )}

        {run.activeMatch && (
          <div style={{ ...bannerStyle, borderColor: '#ffb703', background: 'rgba(255, 183, 3, 0.08)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#ffb703', fontWeight: 'bold', fontSize: 13 }}>⚠ Unfinished match at {nodeLabel(run.activeMatch.targetNodeId)}</div>
              <div style={{ color: '#bbb', fontSize: 11, marginTop: 4 }}>
                Finish it to continue the run. If you forfeit it, or leave it untouched for over an hour, it counts as a loss.
              </div>
            </div>
            <button onClick={resumeMatch} disabled={busy} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}>▶ Resume</button>
            <button onClick={forfeitMatch} disabled={busy} style={{ ...btnDanger, padding: '6px 14px', fontSize: 12 }}>Forfeit (loss)</button>
          </div>
        )}

        {!run.activeMatch && run.pendingAdvanceNodeId && (
          <div style={{ ...bannerStyle, borderColor: '#4ade80', background: 'rgba(74, 222, 128, 0.08)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 13 }}>✓ You won the match at {nodeLabel(run.pendingAdvanceNodeId)}</div>
              <div style={{ color: '#bbb', fontSize: 11, marginTop: 4 }}>Move onto the node to collect its loot and continue.</div>
            </div>
            <button onClick={advancePending} disabled={busy} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}>Advance →</button>
          </div>
        )}

        {run.lastMatchResult && run.lastMatchResult.matchID !== lastSeen && (() => {
          const r = run.lastMatchResult;
          const lost = r.cardsLost.map(c => `${c.suit} ${c.value}`).join(', ');
          const color = r.outcome === 'won' ? '#4ade80' : r.outcome === 'draw' ? '#bbb' : '#e57373';
          const title = r.reason === 'forfeit'
            ? '✗ Last match forfeited — counted as a loss'
            : r.reason === 'interrupted'
              ? '✗ Last match was left unfinished for over an hour — counted as a loss'
              : r.outcome === 'won' ? '✓ Last match won' : r.outcome === 'draw' ? '= Last match drawn — no cards lost, no advance' : '✗ Last match lost';
          return (
            <div style={{ ...bannerStyle, borderColor: color }}>
              <div style={{ flex: 1 }}>
                <div style={{ color, fontWeight: 'bold', fontSize: 13 }}>{title}</div>
                <div style={{ color: '#bbb', fontSize: 11, marginTop: 4 }}>
                  {r.cardsLost.length > 0 ? `Cards lost for good: ${lost}. The deck that lost them is invalid until you repair it at a Safehouse.` : r.outcome === 'won' ? `Cards snatched from the opponent: ${r.cardsLooted}.` : 'No cards lost.'}
                </div>
              </div>
              <button onClick={dismissLastResult} style={{ ...btnNeutral, padding: '4px 10px', fontSize: 11 }}>OK</button>
            </div>
          );
        })()}

        <div className="run-decks-panel" style={{ display: 'flex', gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
          <DeckSnapshotCard slot="A" snap={run.decksSnapshot.A} />
          <DeckSnapshotCard slot="B" snap={run.decksSnapshot.B} />
        </div>

        {/* Map navigation */}
        {run.map ? (
          <div className="run-map-panel" style={{ marginTop: 32 }}>
            <h3 style={{ color: 'var(--accent-color)', fontSize: 14, marginBottom: 10 }}>
              Run Map <span style={{ color: '#888', fontSize: 11, fontWeight: 'normal' }}>(click any node for info / actions)</span>
            </h3>
            <MapView
              map={run.map}
              cardsAccCount={run.cardsAcc.length}
              resourcesAcc={run.resourcesAcc}
              onNodeClick={handleNodeClick}
            />
          </div>
        ) : (
          <div style={{ marginTop: 24, color: '#888', fontSize: 12 }}>
            Run with no map (pre-M2). Abandon and create a new one.
          </div>
        )}
      </div>

      {inspectedNodeId && run.map && (() => {
        const node = run.map.nodes.find(n => n.id === inspectedNodeId);
        if (!node) return null;
        const current = run.map.nodes.find(n => n.id === run.map!.currentNodeId);
        const isAdjacent = !!current?.connections.includes(node.id);
        const isCurrent = node.id === run.map.currentNodeId;
        return (
          <NodeInfoOverlay
            // key sul node id: forza remount quando l'utente passa da un nodo
            // all'altro, così la selezione del mazzo dentro l'overlay si resetta.
            key={node.id}
            node={node}
            isAdjacent={isAdjacent}
            isCurrent={isCurrent}
            decksSnapshot={run.decksSnapshot}
            busy={busy}
            onClose={() => setInspectedNodeId(null)}
            onPlayMatch={handlePlayMatchFromNodeOverlay}
            onAdvanceToExtract={handleAdvanceToExtractFromNodeOverlay}
            onOpenExtractActions={() => { setInspectedNodeId(null); setExtractOpen(true); }}
          />
        );
      })()}

      {extractOpen && (
        <ExtractActionOverlay
          cardsAcc={run.cardsAcc}
          resourcesAcc={run.resourcesAcc}
          cacheOffer={currentExtractNode?.cacheOffer ?? []}
          cacheClaimedId={currentExtractNode?.cacheClaimedId ?? null}
          onClaimCache={claimCache}
          onExtract={doExtract}
          onReconfigure={() => { setExtractOpen(false); setReconfigureOpen(true); }}
          onClose={() => setExtractOpen(false)}
          busy={busy}
        />
      )}

      <ReconfigureOverlay
        open={reconfigureOpen}
        initialDeckA={run.decksSnapshot.A.cards}
        initialDeckB={run.decksSnapshot.B.cards}
        initialLoot={run.cardsAcc}
        token={token}
        onClose={() => setReconfigureOpen(false)}
        onSaved={(updatedRun) => {
          setReconfigureOpen(false);
          onRefresh(updatedRun as UserRunRecord);
          // Riapri l'extract overlay così l'utente può decidere se estrarre o continuare.
          setExtractOpen(true);
        }}
      />

      {/* Refresh hook: parent può richiamarlo passando un dato run aggiornato. Solo se la run è attiva (evita di azzerare schermate terminali) */}
      {run.status === 'active' && (
        <RunRefresher token={token} onRefresh={onRefresh} />
      )}

      {user.settings?.runTutorialSeen === false && (
        <RunTutorialOverlay
          onDismiss={() => onUpdateSettings({ runTutorialSeen: true })}
        />
      )}
    </div>
  );
}

function DeckSnapshotCard({ slot, snap }: { slot: 'A' | 'B'; snap: RunDeckSnapshot }) {
  const suits = Array.from(new Set(snap.cards.map(c => c.suit))) as Suit[];
  return (
    <div style={{
      background: '#1a1a24',
      border: `2px solid ${snap.valid ? 'var(--accent-color)' : '#e57373'}`,
      borderRadius: 8,
      padding: '14px 18px',
      minWidth: 240,
      opacity: snap.valid ? 1 : 0.7,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>
          Deck {slot}: {snap.name}
        </div>
        <div style={{
          fontSize: 11,
          padding: '2px 8px',
          borderRadius: 4,
          background: snap.valid ? '#1a3a1a' : '#3a1010',
          color: snap.valid ? '#4ade80' : '#e57373',
        }}>
          {snap.valid ? `✓ ${snap.cards.length}/14` : `✗ invalid (${snap.cards.length}/14)`}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {suits.map(s => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#bbb' }}>
            <span className={`suit-icon suit-icon-${s.replace(/\s+/g, '-')}`} style={{ width: 16, height: 16 }} />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

// DeckPickerOverlay rimosso — la scelta del mazzo ora avviene dentro NodeInfoOverlay.

// Componente "starter" inline: lista decks dell'utente, pick di 2, POST /auth/runs.
export function RunStarter({
  token, onStarted, onCancel,
}: {
  token: string;
  onStarted: (run: UserRunRecord) => void;
  onCancel: () => void;
}) {
  const [decks, setDecks] = useState<UserDeckSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picks, setPicks] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/decks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) setError(json.error ?? 'load failed');
        else setDecks(json.decks as UserDeckSummary[]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const togglePick = (id: string) => {
    setPicks(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(-2));
  };

  const start = async () => {
    if (picks.length !== 2 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deckIds: picks }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error ?? 'start failed'); return; }
      trackEvent('run_started', { runId: json.run.id });
      onStarted(json.run as UserRunRecord);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ color: 'var(--accent-color)', margin: 0 }}>Start a Run</h2>
        <p style={{ color: '#aaa', fontSize: 13, marginTop: 8 }}>
          Pick <strong>2 decks</strong> from your collection. Only decks built correctly (14 cards, 6 Hand + 1 Mission per each of the 2 suits) can be used.
        </p>
        {loading && <div style={{ color: '#888' }}>Loading decks...</div>}
        {error && <div style={{ color: '#e57373', marginTop: 8 }}>{error}</div>}
        {!loading && decks.length === 0 && (
          <div style={{ color: '#aaa', marginTop: 16 }}>
            You have no decks yet. Go to "Your Cards" and create at least 2 to start a run.
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
          {decks.map(d => {
            const picked = picks.includes(d.id);
            const cards = d.cards.map(c => c.card);
            const handCount = cards.filter(c => c.type === 'Hand').length;
            const missCount = cards.filter(c => c.type === 'Mission').length;
            const suits = Array.from(new Set(cards.map(c => c.suit))) as Suit[];
            // Stessa validazione del server (validateDeckComposition):
            // 14 carte, 2 suit, 6 Hand 1-6 + 1 Mission per suit. Decks
            // non-validi sono unclickable e mostrano il motivo, così
            // startRun non fallisce a metà del POST con un errore criptico.
            const validation = validateDeckComposition(cards);
            const isValid = validation.valid;
            const reason = !isValid ? validation.reason : '';
            return (
              <button
                key={d.id}
                onClick={() => { if (isValid) togglePick(d.id); }}
                disabled={!isValid}
                title={isValid ? '' : `Cannot use: ${reason}`}
                style={{
                  background: picked ? '#1a2a3a' : (isValid ? '#1a1a24' : '#15151a'),
                  border: `2px solid ${picked ? 'var(--accent-color)' : (isValid ? '#444' : '#2a2a2a')}`,
                  borderRadius: 6,
                  padding: '10px 14px',
                  minWidth: 200,
                  textAlign: 'left',
                  cursor: isValid ? 'pointer' : 'not-allowed',
                  color: '#eee',
                  fontFamily: 'inherit',
                  opacity: isValid ? 1 : 0.45,
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                  {picked && '✓ '}{d.name}
                </div>
                <div style={{ fontSize: 11, color: isValid ? '#aaa' : '#777' }}>
                  {cards.length} cards ({handCount}H + {missCount}M)
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  {suits.map(s => (
                    <span key={s} className={`suit-icon suit-icon-${s.replace(/\s+/g, '-')}`} style={{ width: 16, height: 16 }} title={s} />
                  ))}
                </div>
                {!isValid && (
                  <div style={{ color: '#e57373', fontSize: 10, marginTop: 6, lineHeight: 1.3 }}>
                    ⚠ {reason}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnNeutral}>Cancel</button>
          <button
            onClick={start}
            disabled={picks.length !== 2 || busy}
            style={picks.length === 2 ? btnPrimary : { ...btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
          >
            {busy ? 'Starting...' : `Start run (${picks.length}/2 selected)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sub-component: ogni 10s rilegge la run corrente lato server e propaga via
// onRefresh. Utile se l'utente lascia la schermata aperta (es. il server-side
// bot completa un'altra azione). Per ora non strettamente necessario ma low-cost.
function RunRefresher({ token, onRefresh }: { token: string; onRefresh: (r: UserRunRecord | null) => void }) {
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const j = await res.json();
          onRefresh(j.run ?? null);
        }
      } catch { /* silent */ }
    };
    const interval = setInterval(fetchOnce, 10000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  return null;
}

// -------------------- shared styles --------------------

// Layout block (non flex) sul container: serve overflow scroll affidabile.
// Con flex + alignItems flex-start + figlio più alto del parent, alcuni
// browser non scrollano fino in fondo (clipping del bottom del child). Il
// panel si centra orizzontalmente via margin: 0 auto.
const containerStyle: React.CSSProperties = {
  padding: '40px 20px 60px',
  height: '100%',
  overflowY: 'auto',
  boxSizing: 'border-box',
  background: 'var(--bg-color)',
};

// Banner informativi nella vista della run (partita in corso, vittoria da
// riscattare, esito dell'ultima partita).
const bannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginTop: 14,
  padding: '10px 14px',
  border: '1px solid #444',
  borderRadius: 8,
  background: '#15151f',
};

const panelStyle: React.CSSProperties = {
  background: '#0e0e16',
  border: '2px solid var(--accent-color)',
  borderRadius: 10,
  padding: 24,
  maxWidth: 900,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  boxShadow: '0 0 20px rgba(0, 200, 255, 0.2)',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.75)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
};

const btnPrimary: React.CSSProperties = {
  background: 'var(--accent-color)',
  border: 'none',
  color: '#000',
  padding: '10px 22px',
  borderRadius: 6,
  fontWeight: 'bold',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnNeutral: React.CSSProperties = {
  background: '#333',
  border: 'none',
  color: '#bbb',
  padding: '10px 22px',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnDanger: React.CSSProperties = {
  background: 'transparent',
  border: '2px solid #e57373',
  color: '#e57373',
  padding: '10px 22px',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
